import { evaluateExpression } from "./evaluateBindings";
import { createWorldTransform } from "./composeTransforms";
import { resolveAllParameters } from "../utils/parameterSystem";
import { resolvePanelHardwareChildren } from "./resolvePanelHardware";

const PUBLIC_BASE_URL = "https://modularobjects.com";

function toAbsoluteUrl(path) {
  if (!path) return null;
  return String(path).startsWith("http") ? path : `${PUBLIC_BASE_URL}${path}`;
}

function getLinkedGlbPath(definition) {
  const linkedItem =
    definition?.linkedItem ||
    definition?.linked_item ||
    null;

  const linkedAsset =
    linkedItem?.asset ||
    linkedItem?.linked_asset ||
    linkedItem?.linkedAsset ||
    linkedItem?.primary_asset ||
    linkedItem?.primaryAsset ||
    null;

  return (
    linkedItem?.converted_file_path ||
    linkedItem?.convertedFilePath ||
    linkedItem?.converted_file ||
    linkedItem?.convertedFile ||

    linkedAsset?.converted_file_path ||
    linkedAsset?.convertedFilePath ||
    linkedAsset?.converted_file ||
    linkedAsset?.convertedFile ||
    linkedAsset?.file_url ||
    null
  );
}

function getLinkedAsset(definition) {
  const linkedItem =
    definition?.linkedItem ||
    definition?.linked_item ||
    null;

  return (
    linkedItem?.asset ||
    linkedItem?.linked_asset ||
    linkedItem?.linkedAsset ||
    linkedItem?.primary_asset ||
    linkedItem?.primaryAsset ||
    linkedItem?.default_asset ||
    linkedItem?.defaultAsset ||
    null
  );
}

function getLinkedGeneratedGeometry(definition) {
  const asset = getLinkedAsset(definition);
  const metadata = getAssetMetadata(asset);

  return metadata.geometry || null;
}

function safeJson(value, fallback = {}) {
  if (!value) return fallback;
  if (typeof value === "object") return value;

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function getAssetMetadata(asset) {
  return safeJson(asset?.metadata_json, {});
}

function getRolePanelBindings(role) {
  const r = String(role || "").toLowerCase();

  return {
    trimLeft: `parent_${r}TrimLeft`,
    trimRight: `parent_${r}TrimRight`,
    trimTop: `parent_${r}TrimTop`,
    trimBottom: `parent_${r}TrimBottom`,
    offsetX: `parent_${r}OffsetX`,
    offsetY: `parent_${r}OffsetY`,
    offsetZ: `parent_${r}OffsetZ`,
  };
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function getMountingCorrection(definition) {
  const linkedItem =
    definition?.linkedItem ||
    definition?.linked_item ||
    null;

  const linkedItemComponents = linkedItem?.components || [];
  const linkedItemMounting =
    linkedItem?.item_mounting ||
    linkedItem?.itemMounting ||
    null;

  const isHardware =
    definition?.nodeType === "hardware" ||
    definition?.node_type === "hardware";

  const hasLinkedItemAssembly =
    isHardware &&
    Boolean(
      linkedItem &&
        (linkedItemComponents.length > 0 || linkedItemMounting)
    );

  // VAŽNO:
  // Ako hardware ima item assembly, Item Mounting transform se primenjuje
  // samo kroz item_assembly_frame node, nikad i kao root mountingCorrection.
  if (hasLinkedItemAssembly) {
    return {
      offset: { x: 0, y: 0, z: 0 },
      rotation: { x: 0, y: 0, z: 0 },
    };
  }

  const mounting =
    definition?.linkedMounting ||
    definition?.linked_mounting ||
    null;

  const referenceFrame = mounting?.reference_frame_json || null;

  if (!referenceFrame) {
    return {
      offset: { x: 0, y: 0, z: 0 },
      rotation: { x: 0, y: 0, z: 0 },
    };
  }

  return {
    offset: {
      x: toNumber(referenceFrame.offset?.x, 0),
      y: toNumber(referenceFrame.offset?.y, 0),
      z: toNumber(referenceFrame.offset?.z, 0),
    },
    rotation: {
      x: toNumber(referenceFrame.defaultRotation?.x, 0),
      y: toNumber(referenceFrame.defaultRotation?.y, 0),
      z: toNumber(referenceFrame.defaultRotation?.z, 0),
    },
  };
}

function getGeometry(definition, effectiveParameters) {
  const geometryType = definition?.geometryType || "none";
const linkedGlbPath = getLinkedGlbPath(definition);
const linkedAsset = getLinkedAsset(definition);
const linkedGeneratedGeometry = getLinkedGeneratedGeometry(definition);

const linkedItem =
  definition?.linkedItem ||
  definition?.linked_item ||
  null;

// if (definition?.nodeType === "hardware" || geometryType === "catalog_glb") {
//   console.log("HARDWARE GEOMETRY DEBUG", {
//     definitionId: definition.id,
//     definitionName: definition.name,
//     linkedItemId: linkedItem?.id,
//     linkedItemAssetId: linkedItem?.asset_id,
//     hasLinkedAsset: Boolean(linkedAsset),
//     linkedAssetId: linkedAsset?.id,
//     hasGeneratedGeometry: Boolean(linkedGeneratedGeometry),
//     linkedGlbPath,
//   });
// }


const linkedItemComponents =
  linkedItem?.components ||
  [];

// VAŽNO:
// Ako hardware definition ima linked item assembly komponente,
// ne renderujemo root catalog_glb.
// Geometrija mora doći iz asset_component children,
// jer samo oni imaju ispravan item assembly + asset mounting transform chain.
if (
  (definition?.nodeType === "hardware" || geometryType === "catalog_glb") &&
  linkedItemComponents.length > 0
) {
  return null;
}

if (
  (definition?.nodeType === "hardware" || geometryType === "catalog_glb") &&
  linkedGlbPath
) {
  return {
    type: "catalog_glb",
    glbUrl: toAbsoluteUrl(linkedGlbPath),
    linkedItem: definition.linkedItem || definition.linked_item || null,
    linkedMounting: definition.linkedMounting || definition.linked_mounting || null,
    placementRules:
      definition.linkedMounting?.placement_rules_json ||
      definition.linked_mounting?.placement_rules_json ||
      {},
    referenceFrame:
      definition.linkedMounting?.reference_frame_json ||
      definition.linked_mounting?.reference_frame_json ||
      {},
    drillingPattern:
      definition.linkedMounting?.drilling_pattern_json ||
      definition.linked_mounting?.drilling_pattern_json ||
      {},
    processing:
      definition.linkedMounting?.processing_json ||
      definition.linked_mounting?.processing_json ||
      {},
  };
}

if (
  (definition?.nodeType === "hardware" || geometryType === "catalog_glb") &&
  linkedGeneratedGeometry
) {
  return {
    type: "generated_asset",
    asset: linkedAsset,
    generatedGeometry: linkedGeneratedGeometry,
    linkedItem: definition.linkedItem || definition.linked_item || null,
    linkedMounting:
      definition.linkedMounting ||
      definition.linked_mounting ||
      null,
    drillingPattern:
      definition.linkedMounting?.drilling_pattern_json ||
      definition.linked_mounting?.drilling_pattern_json ||
      {},
    processing:
      definition.linkedMounting?.processing_json ||
      definition.linked_mounting?.processing_json ||
      {},
  };
}

  if (geometryType === "none") return null;

  if (geometryType === "box") {
    const g = definition?.geometryDefaults || {};

    return {
      type: "box",
      width: evaluateExpression(g.width ?? "width", effectiveParameters, 100),
      height: evaluateExpression(g.height ?? "height", effectiveParameters, 100),
      depth: evaluateExpression(g.depth ?? "depth", effectiveParameters, 18),
    };
  }

  if (geometryType === "profile_extrude") {
    const g = definition?.geometryDefaults || {};

    return {
      type: "profile_extrude",
      profile: definition?.profile || null,
      thicknessExpr: g.thickness ?? "thickness",
      thickness: evaluateExpression(g.thickness ?? "thickness", effectiveParameters, 18),
    };
  }

  return null;
}

function evalTransformVector(vector = {}, context = {}) {
  return {
    x: evaluateExpression(vector.x ?? 0, context, 0),
    y: evaluateExpression(vector.y ?? 0, context, 0),
    z: evaluateExpression(vector.z ?? 0, context, 0),
  };
}

function evalTransformRotation(rotation = {}, context = {}) {
  return {
    x: evaluateExpression(rotation.x ?? 0, context, 0),
    y: evaluateExpression(rotation.y ?? 0, context, 0),
    z: evaluateExpression(rotation.z ?? 0, context, 0),
  };
}

function getAssetGlbPath(asset) {
  return (
    asset?.converted_file_path ||
    asset?.convertedFilePath ||
    asset?.converted_file ||
    asset?.convertedFile ||
    null
  );
}

function getAssetGeneratedGeometry(asset) {
  const metadata = getAssetMetadata(asset);
  return metadata.geometry || null;
}

function getMountingFrameRotation(referenceFrame = {}, role = "") {
  const roleText = String(role || "").toLowerCase();

  if (roleText.includes("right") && referenceFrame.rightRotation) {
    return referenceFrame.rightRotation;
  }

  if (roleText.includes("left") && referenceFrame.leftRotation) {
    return referenceFrame.leftRotation;
  }

  return referenceFrame.defaultRotation || { x: 0, y: 0, z: 0 };
}

function createMountingItemInstance({
  item,
  parentKey,
  parentScope,
  parentMountingFrame = null,
  parentMountingFrames = [],
  parentAssetId = null,
  parentItemAssetId = null,
  parentComponentItemId = null,
}) {
    const asset =
    item.asset ||
    item.linked_asset ||
    item.linkedAsset ||
    item.item?.asset ||
    item.item?.linked_asset ||
    item.item?.linkedAsset ||
    null;

  return {
  id: `mounting_item_${parentScope}_${item.id}_${parentKey}`,
  nodeType: "mounting_item",
  category: "hardware.mounting_item",
  role: item.role || "mounting_item",

  mountingItemId: item.id,
  mounting_scope: item.mounting_scope || parentScope,
  mounting_id: item.mounting_id,
  item_id: item.item_id,

  parentAssetId,
  parentItemAssetId,
  parentComponentItemId,

  item: item.item || null,
  asset,

  quantity: Number(item.quantity || 1),
  position_json: item.position_json || { x: 0, y: 0, z: 0 },
  rotation_json: item.rotation_json || { x: 0, y: 0, z: 0 },
  parameters_json: item.parameters_json || {},

  processing_enabled: item.processing_enabled !== false,
  bom_enabled: item.bom_enabled !== false,

  parentMountingFrame,
parentMountingFrames:
  Array.isArray(parentMountingFrames) && parentMountingFrames.length
    ? parentMountingFrames
    : parentMountingFrame
    ? [parentMountingFrame]
    : [],

  children: [],
};
}

function resolveItemAssemblyFrameNode({
  instanceNode,
  definitionMap,
  globalParameters = {},
  parentParameters = {},
  parentWorldTransform,
  visitedPath = [],
}) {
  const params = {
    ...(globalParameters || {}),
    ...(parentParameters || {}),
    ...(instanceNode.parameterOverrides || {}),
  };

  const localTransform = {
    position: evalTransformVector(
      instanceNode.transform?.position || { x: 0, y: 0, z: 0 },
      params
    ),
    rotation: evalTransformRotation(
      instanceNode.transform?.rotation || { x: 0, y: 0, z: 0 },
      params
    ),
  };

  const worldTransform = createWorldTransform(
    parentWorldTransform,
    localTransform,
    params
  );

  const children = (instanceNode.children || [])
    .map((child) =>
      resolveOne({
        instanceNode: child,
        definitionMap,
        globalParameters,
        parentParameters: params,
        parentWorldTransform: worldTransform,
        visitedPath,
      })
    )
    .filter(Boolean);

  return {
    id: instanceNode.id,
    definitionId: null,
    name: instanceNode.name || "Item assembly frame",
    nodeType: "item_assembly_frame",
    category: "hardware.item_assembly_frame",
    role: instanceNode.role || "item_assembly_frame",

    source: instanceNode.source || null,
    item_mounting: instanceNode.item_mounting || null,

    effectiveParameters: params,
    parameterOverrides: instanceNode.parameterOverrides || {},
    parameterBindings: {},

    worldTransform,
    parentWorldTransform,

    geometry: null,
    children,
  };
}

function resolveMountingItemNode({
  instanceNode,
  globalParameters = {},
  parentParameters = {},
  parentWorldTransform,
}) {
  const params = {
    ...(globalParameters || {}),
    ...(parentParameters || {}),
    ...(instanceNode.parameters_json || {}),
    ...(instanceNode.parameterOverrides || {}),
  };

  let effectiveParentWorldTransform = parentWorldTransform;

const parentMountingFrames = Array.isArray(instanceNode.parentMountingFrames)
  ? instanceNode.parentMountingFrames
  : instanceNode.parentMountingFrame
  ? [instanceNode.parentMountingFrame]
  : [];

parentMountingFrames.forEach((frame) => {
  if (!frame) return;

  const frameTransform = {
    position: evalTransformVector(
      frame.offset || { x: 0, y: 0, z: 0 },
      params
    ),
    rotation: evalTransformRotation(
      frame.defaultRotation || { x: 0, y: 0, z: 0 },
      params
    ),
  };

  effectiveParentWorldTransform = createWorldTransform(
    effectiveParentWorldTransform,
    frameTransform,
    params
  );
});

  const localTransform = {
    position: evalTransformVector(
      instanceNode.position_json ||
        instanceNode.transform?.position ||
        { x: 0, y: 0, z: 0 },
      params
    ),
    rotation: evalTransformRotation(
      instanceNode.rotation_json ||
        instanceNode.transform?.rotation ||
        { x: 0, y: 0, z: 0 },
      params
    ),
  };

  const worldTransform = createWorldTransform(
    effectiveParentWorldTransform,
    localTransform,
    params
  );

  const asset = instanceNode.asset || null;
  const glbPath = getAssetGlbPath(asset);
  const generatedGeometry = getAssetGeneratedGeometry(asset);
  const hasRenderableAsset = Boolean(glbPath || generatedGeometry);


  return {
    id: instanceNode.id,
    definitionId: null,

    name: instanceNode.item?.name || instanceNode.role || "Mounting item",
    nodeType: "mounting_item",
    category: "hardware.mounting_item",
    role: instanceNode.role || "mounting_item",

    source: {
  mountingItemId: instanceNode.mountingItemId || null,
  mountingScope: instanceNode.mounting_scope || null,
  mountingId: instanceNode.mounting_id || null,

  // Ovo je item samog mounting item-a, npr. screw item
  itemId: instanceNode.item_id || null,

  // Ovo je asset samog mounting item-a, npr. screw generated asset
  assetId: asset?.id || null,

  // Ovo su parent izvori gde je mounting item dodat
  parentAssetId: instanceNode.parentAssetId || null,
  parentItemAssetId: instanceNode.parentItemAssetId || null,
  parentComponentItemId: instanceNode.parentComponentItemId || null,
},

    item: instanceNode.item || null,
    asset,

    quantity: Number(instanceNode.quantity || 1),
    bom_enabled: instanceNode.bom_enabled !== false,
    processing_enabled: instanceNode.processing_enabled !== false,

    effectiveParameters: params,
    parameterOverrides: instanceNode.parameters_json || {},
    parameterBindings: {},

    worldTransform,
    parentWorldTransform: effectiveParentWorldTransform,

    geometry: hasRenderableAsset
  ? {
      type: glbPath ? "asset_glb" : "generated_asset",
      asset,
      glbUrl: glbPath ? toAbsoluteUrl(glbPath) : null,
      generatedGeometry,
    }
  : {
      type: "mounting_marker",
      asset: null,
      radius: 6,
    },

    children: [],
  };
}

function resolveAssetComponentNode({
  instanceNode,
  definitionMap,
  globalParameters = {},
  parentParameters = {},
  parentWorldTransform,
  visitedPath = [],
}) {
  const meta = instanceNode.metadata_json || {};

  if (meta.enabled === false) {
    return null;
  }

  const componentParameters = {
    ...(globalParameters || {}),
    ...(parentParameters || {}),
    ...(meta.parameters || {}),
    ...(instanceNode.parameterOverrides || {}),
  };

  let effectiveParentWorldTransform = parentWorldTransform;

const parentMountingFrame = instanceNode.parentMountingFrame || null;

if (parentMountingFrame) {
  const parentFrameTransform = {
    position: evalTransformVector(
      parentMountingFrame.offset || { x: 0, y: 0, z: 0 },
      componentParameters
    ),
    rotation: evalTransformRotation(
      parentMountingFrame.defaultRotation || { x: 0, y: 0, z: 0 },
      componentParameters
    ),
  };

  effectiveParentWorldTransform = createWorldTransform(
    parentWorldTransform,
    parentFrameTransform,
    componentParameters
  );
}

const componentLocalTransform = {
  position: evalTransformVector(
    meta.position ||
      instanceNode.transform?.position ||
      { x: 0, y: 0, z: 0 },
    componentParameters
  ),
  rotation: evalTransformRotation(
    meta.rotation ||
      instanceNode.transform?.rotation ||
      { x: 0, y: 0, z: 0 },
    componentParameters
  ),
};

const componentWorldTransform = createWorldTransform(
  effectiveParentWorldTransform,
  componentLocalTransform,
  componentParameters
);

const componentItemMounting =
  instanceNode.component_item?.item_mounting ||
  instanceNode.component_item?.itemMounting ||
  instanceNode.item_mounting ||
  null;

const componentItemFrame = componentItemMounting?.reference_frame_json || {};

const assetMounting = instanceNode.asset_mounting || null;
const referenceFrame = assetMounting?.reference_frame_json || {};

const hasComponentItemMounting = Boolean(componentItemMounting);

const componentItemDrillingPattern =
  componentItemMounting?.drilling_pattern_json || {};

const assetDrillingPattern =
  assetMounting?.drilling_pattern_json || {};

const modelFrame = hasComponentItemMounting
  ? componentItemFrame
  : referenceFrame;

const componentItemModelTransform = {
  position: evalTransformVector(
    componentItemFrame.offset || { x: 0, y: 0, z: 0 },
    componentParameters
  ),
  rotation: evalTransformRotation(
    componentItemFrame.defaultRotation || { x: 0, y: 0, z: 0 },
    componentParameters
  ),
};

const assetModelTransform = {
  position: evalTransformVector(
    referenceFrame.offset || { x: 0, y: 0, z: 0 },
    componentParameters
  ),
  rotation: evalTransformRotation(
    getMountingFrameRotation(referenceFrame, instanceNode.role),
    componentParameters
  ),
};

const localGeometryTransform = createWorldTransform(
  componentItemModelTransform,
  assetModelTransform,
  componentParameters
);

const geometryWorldTransform = createWorldTransform(
  componentWorldTransform,
  localGeometryTransform,
  componentParameters
);

const children = (instanceNode.children || [])
  .map((child) =>
    resolveOne({
      instanceNode: child,
      definitionMap,
      globalParameters,
      parentParameters: componentParameters,
      parentWorldTransform: componentWorldTransform,
      visitedPath,
    })
  )
  .filter(Boolean);

  const asset = instanceNode.asset || null;
  const glbPath = getAssetGlbPath(asset);
  const generatedGeometry = getAssetGeneratedGeometry(asset);

  return {
  id: instanceNode.id,
  definitionId: null,

  name:
    instanceNode.component_item?.name ||
    instanceNode.asset?.name ||
    instanceNode.role ||
    "Asset component",

  nodeType: "asset_component",
  category: "asset.component",
  role: instanceNode.role || null,

  source: instanceNode.source || null,

  asset,
  component_item: instanceNode.component_item || null,
  asset_mounting: assetMounting,

  effectiveParameters: componentParameters,
  parameterOverrides: instanceNode.parameterOverrides || {},
  parameterBindings: {},

  worldTransform: componentWorldTransform,
parentWorldTransform: effectiveParentWorldTransform,

componentPlacementWorldTransform: componentWorldTransform,
geometryWorldTransform,
mountingWorldTransform: geometryWorldTransform,
componentItemMountingWorldTransform: createWorldTransform(
  componentWorldTransform,
  componentItemModelTransform,
  componentParameters
),
assetMountingWorldTransform: geometryWorldTransform,

  geometry: {
  type: glbPath ? "asset_glb" : "generated_asset",
  asset,
  glbUrl: glbPath ? toAbsoluteUrl(glbPath) : null,
  generatedGeometry,

  modelOffset: localGeometryTransform.position,
  modelRotation: localGeometryTransform.rotation,

  componentItemDrillingPattern,
  drillingPattern: assetDrillingPattern,
  processing: assetMounting?.processing_json || {},
},

  children,
};
}

function generateLinkedItemAssemblyChildren({ instanceNode, definition }) {
  const linkedItem = definition.linkedItem || definition.linked_item || null;

  if (!linkedItem) return [];

  const components = linkedItem.components || [];
  const itemMounting = linkedItem.item_mounting || null;
  const itemMountingFrame = itemMounting?.reference_frame_json || {};

  const componentNodes = components.map((component) => {
    const assetMounting = component.asset_mounting || null;

    const componentItemMounting =
  component.component_item?.item_mounting ||
  component.component_item?.itemMounting ||
  component.item_mounting ||
  null;

const componentItemFrame =
  componentItemMounting?.reference_frame_json || null;

const assetMountingFrame =
  assetMounting?.reference_frame_json || null;

  const componentItemMountingItemNodes =
  (componentItemMounting?.items || []).map((item) =>
    createMountingItemInstance({
      item,
      parentKey: `component_item_${component.id}_${instanceNode.id}`,
      parentScope: "component_item_mounting",

      // VAŽNO:
      // item-level elementi iz Item Mounting Editor-a
      // ostaju u component/item mounting origin-u.
      // Ne primenjujemo componentItemFrame offset.
      parentMountingFrames: [],

      parentAssetId: component.asset_id || component.asset?.id || null,
      parentItemAssetId: component.id || null,
      parentComponentItemId: component.component_item?.id || null,
    })
  );

const assetMountingItemNodes = (assetMounting?.items || []).map((item) =>
  createMountingItemInstance({
    item,
    parentKey: `asset_component_${component.id}_${instanceNode.id}`,
    parentScope: "asset_mounting",

    parentMountingFrames: [
      componentItemFrame,
      assetMountingFrame,
    ].filter(Boolean),

    parentAssetId: component.asset_id || component.asset?.id || null,
    parentItemAssetId: component.id || null,
    parentComponentItemId: component.component_item?.id || null,
  })
);

    return {
  id: `asset_component_${component.id}_${instanceNode.id}`,
  nodeType: "asset_component",
  category: "asset.component",
  role: component.asset_role || "component",

  source: {
    type: "item_asset_component",
    itemAssetId: component.id,
    itemId: linkedItem.id,
    assetId: component.asset_id,
  },

  metadata_json: component.metadata_json || {},
  parameterOverrides: component.metadata_json?.parameters || {},

  asset: component.asset || null,
  component_item: component.component_item || null,
  item_mounting: componentItemMounting,
  component_item_mounting: componentItemMounting,
  asset_mounting: assetMounting,

  children: [
    ...componentItemMountingItemNodes,
    ...assetMountingItemNodes,
  ],
};
  });

  const itemMountingItemNodes = (itemMounting?.items || []).map((item) =>
  createMountingItemInstance({
    item,
    parentKey: `linked_item_${linkedItem.id}_${instanceNode.id}`,
    parentScope: "item_mounting",
    parentMountingFrames: [itemMountingFrame].filter(Boolean),
  })
);

  if (!componentNodes.length && !itemMountingItemNodes.length) {
    return [];
  }

  return [
    {
      id: `item_assembly_frame_${linkedItem.id}_${instanceNode.id}`,
      nodeType: "item_assembly_frame",
      category: "hardware.item_assembly_frame",
      role: "item_assembly_frame",
      name: `${linkedItem.code || linkedItem.name || "Item"} assembly frame`,

      source: {
        type: "linked_item_mounting",
        itemId: linkedItem.id,
        itemMountingId: itemMounting?.id || null,
      },

      item_mounting: itemMounting || null,

      transform: {
  position: { x: 0, y: 0, z: 0 },
  rotation: { x: 0, y: 0, z: 0 },
},

      parameterOverrides: {},
      children: [...componentNodes, ...itemMountingItemNodes],
    },
  ];
}

function mergeChildren(definitionChildren = [], instanceChildren = []) {
  const merged = [...definitionChildren, ...instanceChildren];
  const unique = [];
  const seen = new Set();

  for (const child of merged) {
    const key = `${child.id}:${child.definitionId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(child);
  }

  return unique;
}

function applyPlacementSnap({ placement, parentParameters, selfParameters }) {
  if (!placement || placement.mode !== "snap") return null;

  const width = Number(parentParameters.width || 0);
  const height = Number(parentParameters.height || 0);
  const depth = Number(parentParameters.depth || 0);

  const selfD = Number(selfParameters.depth || selfParameters.thickness || 0);

  let result;

  switch (placement.face) {
    case "left":
      result = {
        position: { x: -width / 2 + selfD / 2, y: 0, z: 0 },
        rotation: { x: 0, y: -90, z: 0 },
      };
      break;

    case "right":
      result = {
        position: { x: width / 2 - selfD / 2, y: 0, z: 0 },
        rotation: { x: 0, y: 90, z: 0 },
      };
      break;

    case "top":
      result = {
        position: { x: 0, y: height / 2 - selfD / 2, z: 0 },
        rotation: { x: -90, y: 0, z: 0 },
      };
      break;

    case "bottom":
      result = {
        position: { x: 0, y: -height / 2 + selfD / 2, z: 0 },
        rotation: { x: 90, y: 0, z: 0 },
      };
      break;

    case "back":
      result = {
        position: { x: 0, y: 0, z: -depth / 2 + selfD / 2 },
        rotation: { x: 0, y: 180, z: 0 },
      };
      break;

    case "front":
      result = {
        position: { x: 0, y: 0, z: depth / 2 - selfD / 2 },
        rotation: { x: 0, y: 0, z: 0 },
      };
      break;

    case "center":
    default:
      result = {
        position: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
      };
      break;
  }

  const offset = placement.offset || {};

  let trim = {
  top: Number(selfParameters.trimTop || 0),
  bottom: Number(selfParameters.trimBottom || 0),
  left: Number(selfParameters.trimLeft || 0),
  right: Number(selfParameters.trimRight || 0),
};

if (placement.face === "right") {
  trim = {
    ...trim,
    left: Number(selfParameters.trimRight || 0),
    right: Number(selfParameters.trimLeft || 0),
  };
}

const dx = (trim.left - trim.right) / 2;
const dy = (trim.bottom - trim.top) / 2;

const localOffset = {
  x: Number(selfParameters.offsetX || 0),
  y: Number(selfParameters.offsetY || 0),
  z: Number(selfParameters.offsetZ || 0),
};

result.position.x += localOffset.x;
result.position.y += localOffset.y;
result.position.z += localOffset.z;

if (placement.face === "front" || placement.face === "back") {
  result.position.x += dx;
  result.position.y += dy;
}

if (placement.face === "left" || placement.face === "right") {
  result.position.z += dx;
  result.position.y += dy;
}

if (placement.face === "top" || placement.face === "bottom") {
  result.position.x += dx;
  result.position.z += dy;
}

  function evalOffset(value, context) {
    if (value === undefined || value === null || value === "") return 0;

    const n = Number(value);
    if (!Number.isNaN(n)) return n;

    const evaluated = evaluateExpression(value, context, 0);
    return Number(evaluated || 0);
  }

  const context = {
    ...(parentParameters || {}),
    ...(selfParameters || {}),
  };

  result.position.x += evalOffset(offset.x, context);
  result.position.y += evalOffset(offset.y, context);
  result.position.z += evalOffset(offset.z, context);

  return result;
}

function generateShelfChildren({
  instanceNode,
  definition,
  effectiveParameters,
  boardDefinitionId,
  globalParameters,
}) {
  if (definition.category !== "cabinet.box") return [];
  if (!boardDefinitionId) return [];

  const shelfCount = Number(effectiveParameters.shelfCount || 0);
  if (!shelfCount) return [];

  const width = Number(effectiveParameters.width || 0);
  const height = Number(effectiveParameters.height || 0);
  const depth = Number(effectiveParameters.depth || 0);
  const t = Number(effectiveParameters.boardThickness || 18);

  const shelfThickness = Number(effectiveParameters.shelfThickness || t);

  const defaultTrimLeft = Number(effectiveParameters.shelfTrimLeft || t);
  const defaultTrimRight = Number(effectiveParameters.shelfTrimRight || t);
  const defaultTrimTop = Number(effectiveParameters.shelfTrimTop || t);
  const defaultTrimBottom = Number(effectiveParameters.shelfTrimBottom || t);

  const innerHeight = height - t * 2;
  const spacing = innerHeight / (shelfCount + 1);

  const shelves = [];

  for (let i = 0; i < shelfCount; i += 1) {
    const shelfKey = `shelf_${i + 1}`;
    const shelfOverride = instanceNode.shelfOverrides?.[shelfKey] || {};
    const shelfParams = shelfOverride.parameterOverrides || {};

    const trimLeft =
      shelfParams.trimLeft !== undefined ? Number(shelfParams.trimLeft) : defaultTrimLeft;

    const trimRight =
      shelfParams.trimRight !== undefined ? Number(shelfParams.trimRight) : defaultTrimRight;

    const trimTop =
      shelfParams.trimTop !== undefined ? Number(shelfParams.trimTop) : defaultTrimTop;

    const trimBottom =
      shelfParams.trimBottom !== undefined ? Number(shelfParams.trimBottom) : defaultTrimBottom;

    const offsetX = Number(shelfParams.offsetX || 0);
    const offsetY = Number(shelfParams.offsetY || 0);
    const offsetZ = Number(shelfParams.offsetZ || 0);

    const y = -height / 2 + t + spacing * (i + 1) + offsetY;

    shelves.push({
      id: `generated_shelf_${i + 1}_${instanceNode.id}`,
      definitionId: shelfOverride.definitionId || boardDefinitionId,

      parentGeneratedFromId: instanceNode.id,
      overrideKey: shelfKey,
      panelKey: shelfKey,
      partitionSide: "shelf",
      generatedRole: "shelf",

      materialAssignments:
        shelfOverride.materialAssignments || {
          body:
  instanceNode.materialAssignments?.shelfBoard ||
  materialSlotFromProject(globalParameters, "shelfBoard") ||
  instanceNode.materialAssignments?.corpusBoard ||
  materialSlotFromProject(globalParameters, "corpusBoard") ||
  {},
          edge:
  instanceNode.materialAssignments?.shelfEdge ||
  materialSlotFromProject(globalParameters, "shelfEdge") ||
  instanceNode.materialAssignments?.corpusEdge ||
  materialSlotFromProject(globalParameters, "corpusEdge") ||
  {},
          edges:
            instanceNode.materialAssignments?.shelfEdges ||
            instanceNode.materialAssignments?.corpusEdges ||
            {},
        },

      name: `Shelf ${i + 1}`,
      role: "shelf",
      placement: null,

      parameterBindings: {
  width: String(Math.max(1, width - trimLeft - trimRight)),
  height: String(Math.max(1, depth - trimTop - trimBottom)),
  depth: String(shelfThickness),

  trimLeft: String(trimLeft),
  trimRight: String(trimRight),
  trimTop: String(trimTop),
  trimBottom: String(trimBottom),

  offsetX: String(offsetX),
  offsetY: String(offsetY),
  offsetZ: String(offsetZ),
},

      parameterOverrides: shelfParams,
      parentShelfOverrides: instanceNode.shelfOverrides || {},

      transform: {
        position: {
          x: (trimLeft - trimRight) / 2 + offsetX,
          y,
          z: (trimTop - trimBottom) / 2 + offsetZ,
        },
        rotation: { x: 90, y: 0, z: 0 },
      },

      children: [],
    });
  }

  return shelves;
}

function makeSegments(lines, total) {
  const result = [];
  let prev = 0;

  lines.forEach((offset) => {
    if (offset > prev && offset < total) {
      result.push({ from: prev, to: offset });
      prev = offset;
    }
  });

  result.push({ from: prev, to: total });
  return result;
}

function getPartitionPanelTransform(side, p, panelWidth, panelHeight, width, height, depth, t) {
  const cx = p.fromX + p.rawPanelWidth / 2 + (p.gapOffsetX || 0);
  const cy = p.fromY + p.rawPanelHeight / 2 + (p.gapOffsetY || 0);

  if (side === "front") {
    return {
      position: {
        x: -width / 2 + cx,
        y: -height / 2 + cy,
        z: depth / 2 - t / 2,
      },
      rotation: { x: 0, y: 0, z: 0 },
    };
  }

  if (side === "back") {
    return {
      position: {
        x: -width / 2 + cx,
        y: -height / 2 + cy,
        z: -depth / 2 + t / 2,
      },
      rotation: { x: 0, y: 180, z: 0 },
    };
  }

  if (side === "left") {
    return {
      position: {
        x: -width / 2 + t / 2,
        y: -height / 2 + cy,
        z: -depth / 2 + cx,
      },
      rotation: { x: 0, y: -90, z: 0 },
    };
  }

  if (side === "right") {
    return {
      position: {
        x: width / 2 - t / 2,
        y: -height / 2 + cy,
        z: -depth / 2 + cx,
      },
      rotation: { x: 0, y: 90, z: 0 },
    };
  }

  if (side === "top") {
    return {
      position: {
        x: -width / 2 + cx,
        y: height / 2 - t / 2,
        z: -depth / 2 + cy,
      },
      rotation: { x: -90, y: 0, z: 180 },
    };
  }

  if (side === "bottom") {
    return {
      position: {
        x: -width / 2 + cx,
        y: -height / 2 + t / 2,
        z: -depth / 2 + cy,
      },
      rotation: { x: 90, y: 0, z: 0 },
    };
  }

  return null;
}

function materialSlotFromProject(globalParameters, key) {
  const materialId = globalParameters?.[`${key}MaterialId`];

  if (!materialId) return {};

  return {
    materialId: Number(materialId),
  };
}

function getSideMaterialAssignments(side, instanceNode, override = {}, globalParameters = {}) {
  const overrideAssignments = override.materialAssignments || {};

  if (side === "front") {
    return {
      body:
        overrideAssignments.body ||
        instanceNode.materialAssignments?.frontBoard ||
        materialSlotFromProject(globalParameters, "frontBoard") ||
        {},
      edge:
        overrideAssignments.edge ||
        instanceNode.materialAssignments?.frontEdge ||
        materialSlotFromProject(globalParameters, "frontEdge") ||
        {},
      edges: overrideAssignments.edges || instanceNode.materialAssignments?.frontEdges || {},
    };
  }

  if (side === "back") {
    return {
      body:
        overrideAssignments.body ||
        instanceNode.materialAssignments?.backBoard ||
        materialSlotFromProject(globalParameters, "backBoard") ||
        {},
      edge:
        overrideAssignments.edge ||
        instanceNode.materialAssignments?.backEdge ||
        materialSlotFromProject(globalParameters, "backEdge") ||
        {},
      edges: overrideAssignments.edges || instanceNode.materialAssignments?.backEdges || {},
    };
  }

  if (["left", "right", "top", "bottom"].includes(side)) {
    return {
      body:
        overrideAssignments.body ||
        instanceNode.materialAssignments?.corpusBoard ||
        materialSlotFromProject(globalParameters, "corpusBoard") ||
        {},
      edge:
        overrideAssignments.edge ||
        instanceNode.materialAssignments?.corpusEdge ||
        materialSlotFromProject(globalParameters, "corpusEdge") ||
        {},
      edges: overrideAssignments.edges || instanceNode.materialAssignments?.corpusEdges || {},
    };
  }

  return {
    body: overrideAssignments.body || {},
    edge: overrideAssignments.edge || {},
    edges: overrideAssignments.edges || {},
  };
}

function round3(value) {
  return Number(toNumber(value, 0).toFixed(3));
}

function isHardwareLikeChild(child, definitionMap) {
  const role = String(child?.role || "").toLowerCase();
  const id = String(child?.id || "").toLowerCase();

  const childDefinition = definitionMap.get(String(child?.definitionId));
  const childNodeType = String(
    childDefinition?.nodeType || childDefinition?.node_type || ""
  ).toLowerCase();

  const childCategory = String(childDefinition?.category || "").toLowerCase();
  const childGeometryType = String(
    childDefinition?.geometryType || childDefinition?.geometry_type || ""
  ).toLowerCase();

  return (
    role.includes("hinge") ||
    role.includes("handle") ||
    role.includes("lift") ||
    role.includes("hardware") ||
    id.includes("hinge") ||
    id.includes("handle") ||
    id.includes("lift") ||
    childNodeType === "hardware" ||
    childCategory.includes("hardware") ||
    childGeometryType === "catalog_glb"
  );
}

function dedupeChildrenByHardwarePlacement(children = [], definitionMap) {
  const seenHardware = new Set();

  return children.filter((child) => {
    if (!isHardwareLikeChild(child, definitionMap)) {
      return true;
    }

    const p = child?.transform?.position || {};
    const r = child?.transform?.rotation || {};

    const key = [
      child?.definitionId || "",
      child?.role || "",
      round3(p.x),
      round3(p.y),
      round3(p.z),
      round3(r.x),
      round3(r.y),
      round3(r.z),
    ].join("|");

    if (seenHardware.has(key)) {
      console.warn("Duplicate hardware child removed in resolveNodeTree:", {
        key,
        id: child.id,
        role: child.role,
        definitionId: child.definitionId,
        position: child.transform?.position,
      });

      return false;
    }

    seenHardware.add(key);
    return true;
  });
}

function resolveOne({
  instanceNode,
  definitionMap,
  globalParameters = {},
  parentParameters = {},
  parentWorldTransform,
  visitedPath = [],
  inheritedMaterialAssignments = {},
}) {

  if (instanceNode.nodeType === "item_assembly_frame") {
  return resolveItemAssemblyFrameNode({
    instanceNode,
    definitionMap,
    globalParameters,
    parentParameters,
    parentWorldTransform,
    visitedPath,
  });
}

  if (instanceNode.nodeType === "mounting_item") {
    return resolveMountingItemNode({
      instanceNode,
      globalParameters,
      parentParameters,
      parentWorldTransform,
    });
  }

  if (instanceNode.nodeType === "asset_component") {
    return resolveAssetComponentNode({
      instanceNode,
      definitionMap,
      globalParameters,
      parentParameters,
      parentWorldTransform,
      visitedPath,
    });
  }

  const definition = definitionMap.get(String(instanceNode.definitionId));
  if (!definition) {
    console.warn("Missing definition for instance:", instanceNode);
    return null;
  }

  const currentKey = `${instanceNode.id}:${instanceNode.definitionId}`;

  if (visitedPath.includes(currentKey)) {
  console.warn("Cycle detected in resolveNodeTree:", [
    ...visitedPath,
    currentKey,
  ]);

  return null;
}

 const parameterBindings = {
  ...(instanceNode.parameterBindings || {}),
};

const parentAliases = {};

Object.entries(parentParameters || {}).forEach(([key, value]) => {
  parentAliases[`parent_${key}`] = value;
});

const projectAliases = {};

Object.entries(globalParameters || {}).forEach(([key, value]) => {
  projectAliases[`project_${key}`] = value;
});

const resolvedParams = resolveAllParameters({
  project: {
    ...(globalParameters || {}),
    ...projectAliases,
  },
  type: definition.defaultParameters || {},
  bindings: parameterBindings,
  constraints: {
    ...(definition.constraints || {}),
    ...(instanceNode.constraints || {}),
  },
  instance: instanceNode.parameterOverrides || {},
  parent: {
    ...(parentParameters || {}),
    ...parentAliases,
  },
});

const effectiveParameters = resolvedParams.effectiveParameters;
const parameterDebug = resolvedParams.debug;

const ownMaterialAssignments = instanceNode.materialAssignments || {};

const materialAssignments =
  Object.keys(ownMaterialAssignments).length > 0
    ? ownMaterialAssignments
    : inheritedMaterialAssignments;

let localTransform = instanceNode.transform || {};

if (definition.category === "cabinet.box") {
  localTransform = {
    ...(localTransform || {}),
    position: {
      ...(localTransform.position || {}),
      y:
        Number(effectiveParameters.height || 0) / 2 +
        Number(effectiveParameters.floorOffset || 0),
    },
  };
}

const snap = applyPlacementSnap({
  placement: instanceNode.placement,
  parentParameters,
  selfParameters: effectiveParameters,
});

if (snap) {
  localTransform = {
    position: snap.position,
    rotation: snap.rotation,
  };
}

const worldTransform = createWorldTransform(
  parentWorldTransform,
  localTransform,
  effectiveParameters
);

  const geometry = getGeometry(definition, effectiveParameters);

  const instanceChildren = Array.isArray(instanceNode.children)
  ? instanceNode.children
  : [];

const definitionChildren = Array.isArray(definition.children)
  ? definition.children
  : [];

const baseChildren =
  instanceChildren.length > 0
    ? instanceChildren
    : definitionChildren;

const partitionedSides = ["front", "back", "left", "right", "top", "bottom"].filter(
  (side) =>
    Boolean(instanceNode.partitions?.[side]?.horizontal?.length) ||
    Boolean(instanceNode.partitions?.[side]?.vertical?.length)
);

const rawChildrenToResolve = partitionedSides.length
  ? baseChildren.filter(
      (child) =>
        !partitionedSides.includes(String(child.role || "").toLowerCase())
    )
  : baseChildren;

const childDefinitionOverrides =
  instanceNode.childDefinitionOverrides ||
  instanceNode.parentChildDefinitionOverrides ||
  {};

const childrenToResolve = rawChildrenToResolve.map((child) => {
  const overrideKey = child.role || child.originalId || child.id;
  const override = childDefinitionOverrides[overrideKey];

  const alreadyGenerated = String(child.id || "").startsWith("generated_");
  const childRole = child.role || "child";

  const resolvedChildId = alreadyGenerated
    ? child.id
    : `generated_${childRole}_${instanceNode.id}_${child.id}`;

    const role = String(child.role || "").toLowerCase();

let roleMaterialAssignments = {};

if (["left", "right", "top", "bottom"].includes(role)) {
  roleMaterialAssignments = {
    body:
  instanceNode.materialAssignments?.corpusBoard ||
  materialSlotFromProject(globalParameters, "corpusBoard") ||
  {},
edge:
  instanceNode.materialAssignments?.corpusEdge ||
  materialSlotFromProject(globalParameters, "corpusEdge") ||
  {},
    edges: instanceNode.materialAssignments?.corpusEdges || {},
  };
}

if (role === "back") {
  roleMaterialAssignments = {
    body:
  instanceNode.materialAssignments?.backBoard ||
  materialSlotFromProject(globalParameters, "backBoard") ||
  {},
edge:
  instanceNode.materialAssignments?.backEdge ||
  materialSlotFromProject(globalParameters, "backEdge") ||
  {},
    edges: instanceNode.materialAssignments?.backEdges || {},
  };
}

if (role === "front") {
  roleMaterialAssignments = {
    body:
  instanceNode.materialAssignments?.frontBoard ||
  materialSlotFromProject(globalParameters, "frontBoard") ||
  {},
edge:
  instanceNode.materialAssignments?.frontEdge ||
  materialSlotFromProject(globalParameters, "frontEdge") ||
  {},
    edges: instanceNode.materialAssignments?.frontEdges || {},
  };
}

  return {
    ...child,

    id: resolvedChildId,
    originalId: child.originalId || child.id,

    definitionId: override?.definitionId || child.definitionId,

    parameterOverrides: {
  ...(child.parameterOverrides || {}),
  ...(override?.parameterOverrides || {}),
},

     materialAssignments: {
    ...roleMaterialAssignments,
    ...(override?.materialAssignments || {}),
    ...(child.materialAssignments || {}),
  },

    parentGeneratedFromId:
      child.parentGeneratedFromId || instanceNode.parentGeneratedFromId || instanceNode.id,

    overrideKey,
    parentChildDefinitionOverrides: childDefinitionOverrides,

    panelKey: child.panelKey || instanceNode.panelKey || null,
    parentPartitions: child.parentPartitions || instanceNode.parentPartitions || null,
  };
});

  const boardDefinitionId =
  baseChildren.find((child) => String(child.role || "").includes("left"))
    ?.definitionId ||
  childrenToResolve.find((child) => String(child.role || "").includes("left"))
    ?.definitionId;

   // 🔥 GENERISANJE FRONT PANELA IZ PARTITIONS (GRID)
let generatedChildren = [];

if (definition.category === "cabinet.box") {
  const height = Number(effectiveParameters.height || 0);
  const width = Number(effectiveParameters.width || 0);
  const depth = Number(effectiveParameters.depth || 0);
  const t = Number(effectiveParameters.boardThickness || 18);

 ["front", "back", "left", "right", "top", "bottom"].forEach((side) => {
  const hLines = instanceNode.partitions?.[side]?.horizontal || [];
  const vLines = instanceNode.partitions?.[side]?.vertical || [];

  if (!hLines.length && !vLines.length) return;

  const sideWidth =
  side === "left" || side === "right" ? depth : width;

const sideHeight =
  side === "top" || side === "bottom" ? depth : height;

  const sortedH = [...hLines]
    .map((l) => Number(l.offset || 0))
    .sort((a, b) => a - b);

  const sortedV = [...vLines]
    .map((l) => Number(l.offset || 0))
    .sort((a, b) => a - b);

  function makeSegments(lines, total) {
    const result = [];
    let prev = 0;

    lines.forEach((offset) => {
      if (offset > prev && offset < total) {
        result.push({ from: prev, to: offset });
        prev = offset;
      }
    });

    result.push({ from: prev, to: total });
    return result;
  }

 const rowSegments = makeSegments(sortedH, sideHeight);
const colSegments = makeSegments(sortedV, sideWidth);

  // 🔥 BUILD GRID CELLS
const cells = [];

rowSegments.forEach((row, rowIndex) => {
  colSegments.forEach((col, colIndex) => {
    cells.push({
      rowIndex,
      colIndex,
      fromY: row.from,
      toY: row.to,
      fromX: col.from,
      toX: col.to,
      merged: false,
    });
  });
});

// 🔥 HELPER: da li postoji linija između dve ćelije
function isVerticalBlocked(colIndex, rowIndex) {
  const line = vLines.find(
    (l) => Number(l.offset || 0) === sortedV[colIndex]
  );
  if (!line) return false;

  const disabled = line.disabledSegments || [];
  return disabled.includes(`row_${rowIndex}`);
}

function isHorizontalBlocked(rowIndex, colIndex) {
  const line = hLines.find(
    (l) => Number(l.offset || 0) === sortedH[rowIndex]
  );
  if (!line) return false;

  const disabled = line.disabledSegments || [];
  return disabled.includes(`col_${colIndex}`);
}


// 🔥 MERGE PASS
const mergedPanels = [];

cells.forEach((cell) => {
  if (cell.merged) return;

  let group = [cell];

  // PROBAJ DESNO MERGE (vertical)
  let nextCol = cell.colIndex + 1;
  while (true) {
    const next = cells.find(
      (c) =>
        c.rowIndex === cell.rowIndex &&
        c.colIndex === nextCol &&
        !c.merged
    );

    if (!next) break;
    if (!isVerticalBlocked(nextCol - 1, cell.rowIndex)) break;

    group.push(next);
    nextCol++;
  }

  // PROBAJ DOLE MERGE (horizontal)
  let nextRow = cell.rowIndex + 1;
  while (true) {
    const nextRowCells = group.map((g) =>
      cells.find(
        (c) =>
          c.rowIndex === nextRow &&
          c.colIndex === g.colIndex &&
          !c.merged
      )
    );

    if (nextRowCells.some((c) => !c)) break;

    if (
      nextRowCells.some((_, i) =>
        !isHorizontalBlocked(nextRow - 1, group[i].colIndex)
      )
    ) {
      break;
    }

    group = [...group, ...nextRowCells];
    nextRow++;
  }

  // MARK merged
  group.forEach((g) => (g.merged = true));

  // COMPUTE BOUNDS
  const minX = Math.min(...group.map((g) => g.fromX));
  const maxX = Math.max(...group.map((g) => g.toX));
  const minY = Math.min(...group.map((g) => g.fromY));
  const maxY = Math.max(...group.map((g) => g.toY));

  const minRow = Math.min(...group.map((g) => g.rowIndex));
const maxRow = Math.max(...group.map((g) => g.rowIndex));
const minCol = Math.min(...group.map((g) => g.colIndex));
const maxCol = Math.max(...group.map((g) => g.colIndex));

mergedPanels.push({
  panelKey: `r${minRow}-${maxRow}_c${minCol}-${maxCol}`,
  fromX: minX,
  toX: maxX,
  fromY: minY,
  toY: maxY,
});
});

const panelOverrides =
  instanceNode.partitions?.[side]?.panelOverrides || {};

// 🔥 GENERATE PANELS
mergedPanels.forEach((p, index) => {

const isFront = side === "front";

const gapOuter = isFront ? Number(effectiveParameters.frontGapOuter || 0) : 0;
const gapInner = isFront ? Number(effectiveParameters.frontGapInner || 0) : 0;

const sideTrimLeft = Number(effectiveParameters[`${side}TrimLeft`] || 0);
const sideTrimRight = Number(effectiveParameters[`${side}TrimRight`] || 0);
const sideTrimTop = Number(effectiveParameters[`${side}TrimTop`] || 0);
const sideTrimBottom = Number(effectiveParameters[`${side}TrimBottom`] || 0);

const mirrorX = side === "right" || side === "back";

// vizuelna X osa
const visualFromX = mirrorX ? sideWidth - p.toX : p.fromX;
const visualToX = mirrorX ? sideWidth - p.fromX : p.toX;

const usableSideWidth = Math.max(
  1,
  sideWidth - sideTrimLeft - sideTrimRight
);

const usableSideHeight = Math.max(
  1,
  sideHeight - sideTrimTop - sideTrimBottom
);

const scaleX = usableSideWidth / sideWidth;
const scaleY = usableSideHeight / sideHeight;

const visualTrimmedFromX = sideTrimLeft + visualFromX * scaleX;
const visualTrimmedToX = sideTrimLeft + visualToX * scaleX;

// vrati nazad u lokalnu osu scene
const trimmedFromX = mirrorX
  ? sideWidth - visualTrimmedToX
  : visualTrimmedFromX;

const trimmedToX = mirrorX
  ? sideWidth - visualTrimmedFromX
  : visualTrimmedToX;

const trimmedFromY = sideTrimBottom + p.fromY * scaleY;
const trimmedToY = sideTrimBottom + p.toY * scaleY;

const rawPanelWidth = trimmedToX - trimmedFromX;
const rawPanelHeight = trimmedToY - trimmedFromY;

const isFirstCol = p.fromX === 0;
const isLastCol = p.toX === sideWidth;
const isFirstRow = p.fromY === 0;
const isLastRow = p.toY === sideHeight;

const gapLeft = isFirstCol ? gapOuter : gapInner / 2;
const gapRight = isLastCol ? gapOuter : gapInner / 2;
const gapBottom = isFirstRow ? gapOuter : gapInner / 2;
const gapTop = isLastRow ? gapOuter : gapInner / 2;

const panelWidth = Math.max(
  1,
  rawPanelWidth - gapLeft - gapRight
);

const panelHeight = Math.max(
  1,
  rawPanelHeight - gapTop - gapBottom
);

const gapOffsetX = (gapLeft - gapRight) / 2;
const gapOffsetY = (gapBottom - gapTop) / 2;

const totalOffsetX = gapOffsetX;
const totalOffsetY = gapOffsetY;

  const override = panelOverrides[p.panelKey] || {};
const panelDefinitionId = override.definitionId || boardDefinitionId;

const panelParameterOverrides = {
  ...(override.parameterOverrides || {}),

  ...(override.panelBehavior
    ? { panelBehavior: override.panelBehavior }
    : {}),

  ...(override.hardwareMode
    ? { hardwareMode: override.hardwareMode }
    : {}),

 ...(override.showHandle !== undefined
  ? {
      handleEnabled: override.showHandle ? 1 : 0,
    }
  : {}),

  ...(override.hingeCount !== undefined
  ? { hingeCount: override.hingeCount }
  : {}),

...(override.hingeTopOffset !== undefined
  ? { hingeTopOffset: override.hingeTopOffset }
  : {}),

...(override.hingeBottomOffset !== undefined
  ? { hingeBottomOffset: override.hingeBottomOffset }
  : {}),

...(override.handleOffsetX !== undefined
  ? { handleOffsetX: override.handleOffsetX }
  : {}),

...(override.handleOffsetY !== undefined
  ? { handleOffsetY: override.handleOffsetY }
  : {}),

...(override.handleOffsetZ !== undefined
  ? { handleOffsetZ: override.handleOffsetZ }
  : {}),

  ...(override.handlePlacementMode !== undefined
  ? { handlePlacementMode: override.handlePlacementMode }
  : {}),

...(override.handleRotationX !== undefined
  ? { handleRotationX: override.handleRotationX }
  : {}),

...(override.handleRotationY !== undefined
  ? { handleRotationY: override.handleRotationY }
  : {}),

...(override.handleRotationZ !== undefined
  ? { handleRotationZ: override.handleRotationZ }
  : {}),

  ...(override.handleDefinitionId
    ? {
        handleDefinitionId: override.handleDefinitionId,
        handleEnabled: override.showHandle === false ? 0 : 1,
      }
    : {}),

  ...(override.hingeDefinitionId
    ? {
        hingeDefinitionId: override.hingeDefinitionId,
        hingeCount: override.hingeCount || 2,
        hingeSide:
          override.hardwareMode === "hinged_right"
            ? "right"
            : "left",
      }
    : {}),

  ...(override.liftDefinitionId
    ? {
        liftDefinitionId: override.liftDefinitionId,
      }
    : {}),

...(override.panelBehavior === "drawer" || override.hardwareMode === "drawer"
  ? {
      panelBehavior: "drawer",
      hardwareMode: "drawer",

      drawerRunnerDefinitionId: override.drawerRunnerDefinitionId || null,

      drawerOffsetLeft:
        override.drawerOffsetLeft !== undefined &&
        override.drawerOffsetLeft !== null
          ? override.drawerOffsetLeft
          : 16,

      drawerOffsetRight:
        override.drawerOffsetRight !== undefined &&
        override.drawerOffsetRight !== null
          ? override.drawerOffsetRight
          : 16,

      drawerOffsetBottom:
        override.drawerOffsetBottom !== undefined &&
        override.drawerOffsetBottom !== null
          ? override.drawerOffsetBottom
          : 18,

      drawerOffsetTop:
        override.drawerOffsetTop !== undefined &&
        override.drawerOffsetTop !== null
          ? override.drawerOffsetTop
          : 20,

      sideClearance:
        override.sideClearance !== undefined &&
        override.sideClearance !== null
          ? override.sideClearance
          : 7.5,

      runnerHeight:
        override.runnerHeight !== undefined &&
        override.runnerHeight !== null
          ? override.runnerHeight
          : 25,

      runnerLength:
        override.runnerLength !== undefined &&
        override.runnerLength !== null
          ? override.runnerLength
          : 300,

      // formule koje se dalje evaluiraju kroz parameterSystem
      drawerWidth:
        "width - drawerOffsetLeft - drawerOffsetRight",

      drawerBoxHeight:
        "height - drawerOffsetBottom - drawerOffsetTop",

      drawerDepthReduction:
  override.drawerDepthReduction !== undefined &&
  override.drawerDepthReduction !== null
    ? override.drawerDepthReduction
    : 10,

drawerDepth: "runnerLength - drawerDepthReduction",

        drawerSideThickness:
  override.drawerSideThickness !== undefined &&
  override.drawerSideThickness !== null
    ? override.drawerSideThickness
    : 16,

drawerBottomThickness:
  override.drawerBottomThickness !== undefined &&
  override.drawerBottomThickness !== null
    ? override.drawerBottomThickness
    : 18,

drawerBottomOffset:
  override.drawerBottomOffset !== undefined &&
  override.drawerBottomOffset !== null
    ? override.drawerBottomOffset
    : 10,

    drawerSideDropBelowRunner:
  override.drawerSideDropBelowRunner !== undefined &&
  override.drawerSideDropBelowRunner !== null
    ? override.drawerSideDropBelowRunner
    : 10,

drawerFrontBackThickness:
  override.drawerFrontBackThickness !== undefined &&
  override.drawerFrontBackThickness !== null
    ? override.drawerFrontBackThickness
    : 16,
    }
  : {}),
};

  if (!panelDefinitionId) return;

  generatedChildren.push({
  id: `generated_${side}_${p.panelKey}_${instanceNode.id}`,
  panelKey: p.panelKey,
  parentGeneratedFromId: instanceNode.id,
  parentPartitions: instanceNode.partitions || null,
  definitionId: panelDefinitionId,
  name: `${side} panel ${index + 1}`,
role: side,
partitionSide: side,
  placement: null,

  materialAssignments: getSideMaterialAssignments(
  side,
  instanceNode,
  override,
  globalParameters
),

    parameterBindings: {
      width: String(panelWidth),
      height: String(panelHeight),
      depth: String(t),
    },
    panelBehavior: override.panelBehavior || null,
hardwareMode: override.hardwareMode || null,
showHandle: override.showHandle,

handleDefinitionId: override.handleDefinitionId || null,
hingeDefinitionId: override.hingeDefinitionId || null,
liftDefinitionId: override.liftDefinitionId || null,

parameterOverrides: panelParameterOverrides,
    transform: getPartitionPanelTransform(
  side,
  {
  ...p,
  fromX: trimmedFromX,
  toX: trimmedToX,
  fromY: trimmedFromY,
  toY: trimmedToY,
  gapOffsetX: totalOffsetX,
  gapOffsetY: totalOffsetY,
  rawPanelWidth,
  rawPanelHeight,
},
  panelWidth,
  panelHeight,
  width,
  height,
  depth,
  t
),
    children: [],
  });
});
});
}

const explicitHardwareCandidates = [
  ...(Array.isArray(instanceNode.children) ? instanceNode.children : []),
  ...(Array.isArray(definition.children) ? definition.children : []),
  ...(Array.isArray(baseChildren) ? baseChildren : []),
  ...(Array.isArray(rawChildrenToResolve) ? rawChildrenToResolve : []),
  ...(Array.isArray(childrenToResolve) ? childrenToResolve : []),
];

const hasExplicitHardwareChildren = explicitHardwareCandidates.some((child) =>
  isHardwareLikeChild(child, definitionMap)
);

generatedChildren = [
  ...generatedChildren,
  ...generateShelfChildren({
    instanceNode,
    definition,
    effectiveParameters,
    boardDefinitionId,
    globalParameters,
  }),

  ...(hasExplicitHardwareChildren
    ? []
    : resolvePanelHardwareChildren({
        instanceNode,
        definition,
        effectiveParameters,
        definitionMap,
      })),

  ...generateLinkedItemAssemblyChildren({
    instanceNode,
    definition,
    effectiveParameters,
  }),
];

  const allChildren = dedupeChildrenByHardwarePlacement(
  [...childrenToResolve, ...generatedChildren],
  definitionMap
);

const children = allChildren
    .map((child) =>
      resolveOne({
  instanceNode: child,
  definitionMap,
  globalParameters,
  parentParameters: effectiveParameters,
  parentWorldTransform: worldTransform,
  visitedPath: [...visitedPath, currentKey],
  inheritedMaterialAssignments: materialAssignments,
})
    )
    .filter(Boolean);

  return {
  id: instanceNode.id,
  definitionId: instanceNode.definitionId,
  name: instanceNode.name || definition.name || definition.code,
  nodeType: definition.nodeType || "element",
  category: definition.category || null,

  overrideKey: instanceNode.overrideKey || null,
parentChildDefinitionOverrides:
  instanceNode.parentChildDefinitionOverrides || null,

 panelKey: instanceNode.panelKey || null,
partitionSide: instanceNode.partitionSide || null,
parentGeneratedFromId: instanceNode.parentGeneratedFromId || null,
  parentPartitions: instanceNode.parentPartitions || null,

  definition,

  mountingCorrection: getMountingCorrection(definition),

  effectiveParameters,

  parameterOverrides: instanceNode.parameterOverrides || {},
parameterBindings: instanceNode.parameterBindings || {},

  ownParameterKeys: Array.from(
  new Set([
    ...Object.keys(definition.defaultParameters || {}),
    ...Object.keys(parameterBindings || {}),
    ...Object.keys(instanceNode.parameterOverrides || {}),
    ...Object.keys(instanceNode.constraints || {}),
  ])
),

  resolvedParameters: Object.entries(effectiveParameters || {}).map(
  ([key, value]) => ({
    key,
    value,
    source: parameterDebug?.[key]?.source || "resolved",
    raw: parameterDebug?.[key]?.raw,
    error: parameterDebug?.[key]?.error,
  })
),

parameterDebug: {
  globalParameters,
  parentParameters,
  definitionDefaults: definition.defaultParameters || {},
  parameterBindings,
  parameterOverrides: instanceNode.parameterOverrides || {},
  effectiveParameters,
  trace: parameterDebug,
  constraints: {
  ...(definition.constraints || {}),
  ...(instanceNode.constraints || {}),
},
},

  worldTransform,
  parentWorldTransform, // 👈 DODATO

  geometry,
  partitions: instanceNode.partitions || null,
  shelfOverrides: instanceNode.shelfOverrides || null,
  parentShelfOverrides: instanceNode.parentShelfOverrides || null,

  materialAssignments,

  children,
};
}

export function resolveNodeTree({
  rootInstances = [],
  definitions = [],
  rootParameters = {},
  globalParameters = {},
}) {
  const definitionMap = new Map(definitions.map((d) => [String(d.id), d]));

  const mergedGlobalParameters = {
    ...(globalParameters || {}),
    ...(rootParameters || {}),
  };

 

  return (rootInstances || [])
    .map((instanceNode) =>
      resolveOne({
        instanceNode,
        definitionMap,
        globalParameters: mergedGlobalParameters,
        parentParameters: mergedGlobalParameters,
        parentWorldTransform: {
          position: { x: 0, y: 0, z: 0 },
          rotation: { x: 0, y: 0, z: 0 },
        },
        visitedPath: [],
      })
    )
    .filter(Boolean);
}